import { buildOmensSiteJsonLd, ResolvedOmensPageSeo } from 'modules/omensSeoTechnical/head'
import Head from 'next/head'
import { createContext, FC, useContext } from 'react'
import { serializeJsonLd } from 'utils/jsonLd'
import config from 'utils/siteConfig'

/** The registry entry of the Omens page being rendered (set by _app), read by CharlesMeta. */
export const OmensSeoContext = createContext<ResolvedOmensPageSeo | undefined>(undefined)

export const useOmensPageSeo = () => useContext(OmensSeoContext)

/**
 * Rendered by _app outside the client-only PersistGate, so every Omens page has its title, description,
 * canonical, robots, Open Graph, the Meta domain verification and site JSON-LD in the server HTML. Same keys and
 * values as CharlesMeta and GeoHead, so next/head keeps one copy whichever renders last.
 */
const OmensSeoHead: FC<{ seo: ResolvedOmensPageSeo }> = ({ seo }) => (
  <Head>
    <title key="title">{seo.title}</title>
    <meta key="description" name="description" content={seo.description} />
    <link key="canonical" rel="canonical" href={seo.canonicalUrl} />
    {seo.noindex && <meta key="robots" name="robots" content="noindex, follow" />}
    <meta key="og:site_name" property="og:site_name" content="Omens" />
    <meta key="og:type" property="og:type" content="website" />
    <meta key="og:locale" property="og:locale" content="pt_BR" />
    <meta key="og:url" property="og:url" content={seo.canonicalUrl} />
    <meta key="og:title" property="og:title" content={seo.title} />
    <meta key="og:description" property="og:description" content={seo.description} />
    <meta key="twitter:title" name="twitter:title" content={seo.title} />
    <meta key="twitter:description" name="twitter:description" content={seo.description} />
    <meta key="twitter:url" name="twitter:url" content={seo.canonicalUrl} />
    <meta
      key="facebook-domain-verification"
      name="facebook-domain-verification"
      content={config.facebookDomainVerification}
    />
    <script
      key="omens-site-jsonld"
      id="omens-site-jsonld"
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: serializeJsonLd(buildOmensSiteJsonLd(seo)) }}
    />
  </Head>
)

export default OmensSeoHead
